<template>
    <div class="date-range-picker" v-click-outside="closePicker">
        <!-- Compact Trigger -->
        <div class="date-picker-trigger" @click="togglePicker" :class="{ open: showPicker }">
            <div class="trigger-left">
                <Calendar :size="15" />
                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(12) }">{{ displayLabel }}</span>
            </div>
            <ChevronDown class="ppv2-w-4 ppv2-h-4 chevron" :class="{ rotate: showPicker }" />
        </div>

        <!-- Popover -->
        <transition name="fade-popover">
            <div v-if="showPicker" class="picker-popover ppv2-bg-white dark:ppv2-bg-dark">
                <div class="popover-content">
                    <!-- Sidebar: Quick Ranges -->
                    <div class="popover-sidebar">
                        <button
                            v-for="option in fullOptions"
                            :key="option.value"
                            class="sidebar-btn ppv2-text-black poppinsRegular"
                            :class="{ active: selectedOption === option.value }"
                            @click="selectOption(option.value)"
                        >
                            {{ option.label }}
                        </button>
                    </div>

                    <!-- Calendar Section -->
                    <div class="popover-calendar-wrapper">
                        <DatePicker
                            v-if="isCalendarVisible"
                            ref="datepicker"
                            v-model="tempRange"
                            range
                            inline
                            :open="true"
                            :popup-style="{ display: 'none' }"
                            :lang="calendarLang"
                            type="date"
                            format="YYYY-MM-DD"
                            :input-attr="{ style: 'display:none' }"
                            class="custom-inline-datepicker"
                            :get-classes="getCalendarClasses"
                        />
                    </div>
                </div>

                <!-- Footer -->
                <div class="popover-footer">
                    <button
                        class="ppv2-min-h-[35px] ppv2-flex ppv2-items-center text-purple ppv2-border-solid border-purple ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-2 ppv2-rounded-xl ppv2-transition-all"
                        @click="closePicker"
                    >
                        <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(14) }">{{
                            $t('statistics_v3.date_picker.cancel')
                        }}</span>
                    </button>
                    <BaseButton
                        variant="purple"
                        :custom-height="35"
                        :custom-font-size="14"
                        custom-class="ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px]"
                        @click="applyRange"
                    >
                        <span class="poppinsMedium">{{ $t('statistics_v3.date_picker.apply') }}</span>
                    </BaseButton>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-vue'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import moment from 'moment'
import 'moment/locale/fr'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import BaseButton from '../../../Shared/BaseButton.vue'
moment.locale('fr')

export default {
    name: 'DateRangePicker',
    directives: {
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
    },
    components: { Calendar, ChevronDown, ChevronLeft, ChevronRight, DatePicker, BaseButton },
    props: {
        value: {
            type: Object,
            default: () => ({
                start: new Date(),
                end: new Date(),
            }),
        },
        excludeNext: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showPicker: false,
            selectedOption: '7days',
            tempRange: [this.value.start, this.value.end],
            currentMonthOffset: 0,
            isCalendarVisible: true,
            appliedRange: null, // Stocke la plage personnalisée appliquée
        }
    },
    computed: {
        displayLabel() {
            if (this.selectedOption === 'custom' && this.appliedRange) {
                return `${moment(this.appliedRange.start).format('DD/MM/YYYY')} - ${moment(this.appliedRange.end).format(
                    'DD/MM/YYYY'
                )}`
            }
            const option = this.fullOptions.find((o) => o.value === this.selectedOption)
            if (option) return option.label
            // fallback
            return `${moment(this.value.start).format('DD/MM/YYYY')} - ${moment(this.value.end).format('DD/MM/YYYY')}`
        },
        fullOptions() {
            const options = [
                { value: 'today', label: this.$t('statistics_v3.date_picker.today') },
                { value: '7next', label: this.$t('statistics_v3.date_picker.next_7_days') },
                { value: '7days', label: this.$t('statistics_v3.date_picker.last_7_days') },
                { value: 'currentMonth', label: this.$t('statistics_v3.date_picker.current_month') },
                { value: 'lastMonth', label: this.$t('statistics_v3.date_picker.last_month') },
                { value: 'currentYear', label: this.$t('statistics_v3.date_picker.current_year') },
                { value: 'lastYear', label: this.$t('statistics_v3.date_picker.last_year') },
            ]
            if (this.excludeNext) {
                return options.filter((opt) => opt.value !== '7next')
            }
            return options
        },
        currentMonthYearLabel() {
            const label = moment().add(this.currentMonthOffset, 'months').format('MMMM YYYY')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
        calendarLang() {
            return {
                formatLocale: {
                    firstDayOfWeek: 1,
                    months: [
                        'Janvier',
                        'Février',
                        'Mars',
                        'Avril',
                        'Mai',
                        'Juin',
                        'Juillet',
                        'Août',
                        'Septembre',
                        'Octobre',
                        'Novembre',
                        'Décembre',
                    ],
                    monthsShort: ['Jan.', 'Fév.', 'Mar.', 'Avr.', 'Mai', 'Juin', 'Jui.', 'Aoû.', 'Sep.', 'Oct.', 'Nov.', 'Déc.'],
                    weekdays: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
                    weekdaysShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
                    weekdaysMin: ['DI', 'LU', 'MA', 'ME', 'JE', 'VE', 'SA'],
                },
            }
        },
    },
    methods: {
        normalizeFontSize,
        moment,
        normalizeSize,
        togglePicker() {
            this.showPicker = !this.showPicker
            if (this.showPicker) {
                this.tempRange = [this.value.start, this.value.end]
            }
        },
        closePicker() {
            this.showPicker = false
        },
        selectOption(option) {
            this.selectedOption = option
            const today = moment()
            let start, end

            switch (option) {
                case 'today':
                    start = today.clone().startOf('day')
                    end = today.clone().endOf('day')
                    break
                case '7next':
                    start = today.clone().startOf('day')
                    end = today.clone().add(6, 'days').endOf('day')
                    break
                case '7days':
                    end = today.clone().endOf('day')
                    start = today.clone().subtract(6, 'days').startOf('day')
                    break
                case 'currentMonth':
                    start = today.clone().startOf('month')
                    end = today.clone().endOf('month')
                    break
                case 'lastMonth':
                    start = today.clone().subtract(1, 'month').startOf('month')
                    end = today.clone().subtract(1, 'month').endOf('month')
                    break
                case 'currentYear':
                    start = today.clone().startOf('year')
                    end = today.clone().endOf('year')
                    break
                case 'lastYear':
                    start = today.clone().subtract(1, 'year').startOf('year')
                    end = today.clone().subtract(1, 'year').endOf('year')
                    break
                default:
                    return
            }

            this.tempRange = [start.toDate(), end.toDate()]
        },
        applyRange() {
            if (this.tempRange && this.tempRange.length === 2) {
                const start = this.tempRange[0]
                const end = this.tempRange[1]
                // Vérifier si la plage correspond à une option prédéfinie
                const matchedOption = this.findMatchingOption(start, end)
                if (matchedOption) {
                    this.selectedOption = matchedOption
                    this.appliedRange = null
                } else {
                    this.selectedOption = 'custom'
                    this.appliedRange = { start, end }
                }
                this.$emit('input', { start, end })
                this.$emit('change', { start, end })
            }
            this.showPicker = false
        },
        findMatchingOption(start, end) {
            const today = moment()
            const ranges = {
                today: {
                    start: today.clone().startOf('day'),
                    end: today.clone().endOf('day'),
                },
                '7next': {
                    start: today.clone().startOf('day'),
                    end: today.clone().add(6, 'days').endOf('day'),
                },
                '7days': {
                    start: today.clone().subtract(6, 'days').startOf('day'),
                    end: today.clone().endOf('day'),
                },
                currentMonth: {
                    start: today.clone().startOf('month'),
                    end: today.clone().endOf('month'),
                },
                lastMonth: {
                    start: today.clone().subtract(1, 'month').startOf('month'),
                    end: today.clone().subtract(1, 'month').endOf('month'),
                },
                currentYear: {
                    start: today.clone().startOf('year'),
                    end: today.clone().endOf('year'),
                },
                lastYear: {
                    start: today.clone().subtract(1, 'year').startOf('year'),
                    end: today.clone().subtract(1, 'year').endOf('year'),
                },
            }
            for (const [key, range] of Object.entries(ranges)) {
                if (moment(start).isSame(range.start, 'day') && moment(end).isSame(range.end, 'day')) {
                    // Vérifier que l'option est disponible dans fullOptions
                    if (this.fullOptions.some((o) => o.value === key)) {
                        return key
                    }
                }
            }
            return null
        },
        prevMonth() {
            this.currentMonthOffset--
            this.refreshCalendar()
        },
        nextMonth() {
            this.currentMonthOffset++
            this.refreshCalendar()
        },
        refreshCalendar() {
            this.isCalendarVisible = false
            this.$nextTick(() => {
                this.isCalendarVisible = true
                // After re-mount, we might still need to push the view if it's sticky
                this.$nextTick(() => {
                    if (this.$refs.datepicker) {
                        const targetDate = moment().add(this.currentMonthOffset, 'months').toDate()
                        // Try to access the internal calendar component to change its view
                        if (typeof this.$refs.datepicker.changeCalendarMonth === 'function') {
                            this.$refs.datepicker.changeCalendarMonth(targetDate)
                        }
                    }
                })
            })
        },
        getCalendarClasses(date) {
            if (!this.tempRange || this.tempRange.length < 2) return ''
            const [start, end] = this.tempRange
            const classes = []
            if (moment(date).isSame(start, 'day')) {
                classes.push('first')
            }
            if (moment(date).isSame(end, 'day')) {
                classes.push('last')
            }
            return classes.join(' ')
        },
    },
    created() {
        moment.locale(this.$i18n.locale || 'fr')
    },
    mounted() {
        // Set default to 7 days
        this.selectOption('7days')
    },
}
</script>

<style scoped>
.date-range-picker {
    position: relative;
    display: inline-block;
}

.date-picker-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 200px;
    height: 30px;
    padding: 0 16px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 12px;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;
}

.date-picker-trigger:hover,
.date-picker-trigger.open {
    border-color: white;
    background: white;
    color: #000;
}

.trigger-left {
    display: flex;
    align-items: center;
    gap: 12px;
}
.chevron {
    transition: transform 0.2s ease;
    opacity: 0.7;
}

.chevron.rotate {
    transform: rotate(180deg);
}

.picker-popover {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: 1000;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    overflow: hidden;
    width: 640px;
    display: flex;
    flex-direction: column;
}

.popover-content {
    display: flex;
    min-height: max-content;
}

.popover-sidebar {
    width: max-content;
    border-right: 1px solid #f1f5f9;
    padding: 16px 0;
    display: flex;
    flex-direction: column;
}

.sidebar-btn {
    text-align: left;
    padding: 8px 12px;
    font-size: 12px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
}

.sidebar-btn:hover {
    color: #7d2dbd;
}

.sidebar-btn.active {
    font-family: 'Poppins Medium' !important;
    color: #7d2dbd;
    background: transparent;
    border: 1.5px solid #7d2dbd;
    border-radius: 20px;
    margin: 0 8px;
    padding: 6px 8px;
    max-width: max-content;
}

.popover-calendar-wrapper {
    flex: 1;
    padding: 18px 8px;
}

.calendar-header-custom {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    margin-bottom: 24px;
}

.calendar-header-custom button {
    background: transparent;
    border: none;
    color: #475569;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 4px;
    border-radius: 50%;
    transition: background 0.2s;
}

.calendar-header-custom button:hover {
    background: #7d2dbd1a;
}

.popover-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    border-top: 1px solid #f1f5f9;
}

.btn-cancel {
    background: transparent;
    border: none;
    color: #7d2dbd;
    cursor: pointer;
    font-size: 15px;
    padding: 8px 16px;
    transition: opacity 0.2s;
}

.btn-cancel:hover {
    opacity: 0.7;
}
.mx-calendar .cell:hover {
    background: #7d2dbd1a;
}

.btn-apply {
    background: #7d2dbd;
    color: white;
    border: none;
    border-radius: 12px;
    padding: 12px 32px;
    cursor: pointer;
    font-size: 15px;
    transition:
        transform 0.2s,
        background 0.2s;
}

/* DatePicker Overrides */
.custom-inline-datepicker {
    width: 100% !important;
}

::v-deep .mx-calendar {
    width: 100%;
}

::v-deep .mx-calendar-header {
    height: 40px;
    line-height: 40px;
    display: flex !important;
    align-items: center;
    justify-content: space-between !important;
    padding: 0 40px !important;
}

::v-deep .mx-btn-icon-left {
    color: #475569;
    order: 1 !important;
}

::v-deep .mx-calendar-header-label {
    font-family: 'Poppins SemiBold';
    font-size: clamp(12px, 0.729545vw, 16px);
    color: inherit;
    order: 2 !important;
    display: flex;
}

::v-deep .mx-btn-icon-right {
    color: #475569;
    order: 3 !important;
}

/* Hide year jump buttons to match design */
::v-deep .mx-btn-icon-double-left,
::v-deep .mx-btn-icon-double-right {
    display: none !important;
}
::v-deep .mx-btn:hover {
    background: #7d2dbd !important;
}
::v-deep .mx-calendar-content {
    height: 224px;
}

::v-deep .mx-datepicker-main {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    display: flex !important;
}

::v-deep .mx-table-date th {
    color: #94a3b8 !important;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 700;
    padding: 8px 0 !important;
}

/* Ensure table layout is preserved */
::v-deep .mx-table-date {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
}

::v-deep .mx-table-date tr {
    display: table-row !important;
}

::v-deep .mx-table-date td {
    display: table-cell !important;
    padding: 0 !important;
    vertical-align: middle !important;
}

::v-deep .mx-date-row .cell {
    border-radius: 0;
    font-family: inherit;
    height: 25px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    margin: 0;
}

/* Base background for both in-range and active (start/end) */
::v-deep .mx-date-row .cell.in-range {
    background-color: #7d2dbd1a;
    color: #7d2dbd;
    border-radius: 0;
}

::v-deep .mx-date-row .cell.active {
    background-color: #7d2dbd1a;
    color: white !important;
    border-radius: 0;
}

/* Specific rounding for start and end dates of the range selection */
::v-deep .mx-date-row .cell.active.first {
    border-top-left-radius: 50% !important;
    border-bottom-left-radius: 50% !important;
}

::v-deep .mx-date-row .cell.active.last {
    border-top-right-radius: 50% !important;
    border-bottom-right-radius: 50% !important;
}

/* The dark purple circle for start/end points */
::v-deep .mx-date-row .cell.active::after {
    content: '';
    position: absolute;
    top: 5.5px;
    left: 5.5px;
    right: 0;
    bottom: 0;
    background-color: #7d2dbd;
    border-radius: 50%;
    z-index: -1;
    width: 20px;
    height: 20px;
}

::v-deep .mx-date-row .cell.active {
    color: white !important;
}

/* Ensure active circle is on top of the in-range background if they overlap */
::v-deep .mx-date-row .cell.in-range::after {
    display: none;
}

/* Transitions */
.fade-popover-enter-active,
.fade-popover-leave-active {
    transition:
        opacity 0.2s,
        transform 0.2s;
}

.fade-popover-enter,
.fade-popover-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
@media (max-width: 768px) {
    ::v-deep .picker-popover {
        left: 0;
        width: calc(100vw - 40px);
    }
    ::v-deep .popover-calendar-wrapper {
        padding: 12px 6px;
    }
    ::v-deep .mx-calendar-header {
        height: 30px;
    }
}
</style>
